/**
 * UI SOUND CUES FOR THE SHELL — the hover/tap/purchase layer, and the ONE
 * delegated listener that drives it.
 *
 * ── WHY THIS FILE EXISTS AT ALL ─────────────────────────────────────────────
 * The project already owns a complete one-shot mixer: `engine/match/audio` has
 * a sound table, a voice pool with per-category caps, retrigger guards, a
 * ducking curve and — because Phase 2 shipped with no recordings — a
 * PROCEDURAL SYNTH for every id in it. `ui_tap`, `ui_denied`, `card_lift`,
 * `card_drop`, `mana_pip`, `mana_full` are all in that table and all audible
 * today with no asset files whatsoever. Writing a second oscillator here would
 * have been a parallel sound system with its own volume curve and its own mute,
 * which is exactly the thing the owner's Settings panel could not then control.
 *
 * So this file is a FACADE, not a synth: it names the cues the meta UI fires in
 * product terms (`hover`, `tap`, `purchase`, …), maps each onto an existing
 * `SoundId`, and owns exactly one `AudioEngine` for the shell.
 *
 * ── ADR-0005: THE IMPORT IS DYNAMIC, AND IT HAS TO BE ───────────────────────
 * `engine/match/**` may never be statically reachable from the entry chunk —
 * `vite.config.ts`'s bundle-layer guard fails the build if it is, because that
 * is how Pixi ends up in the 150 KB shell budget. The mixer itself pulls no
 * Pixi, but the rule is enforced by PATH, and the rule is right: a visitor who
 * never makes a sound should not download a mixer. Hence `await import()`, run
 * on the first user gesture and never before it.
 *
 * ── THE AUTOPLAY LOCK, AND THE ONE SUBTLETY IN IT ───────────────────────────
 * No browser lets audio start outside a user gesture. `AudioEngine.unlock()`
 * must therefore be reached from inside one. But `await import()` ends the
 * gesture's task, so the engine cannot be built AND unlocked in the same tick
 * as the first click. Both halves are covered:
 *   · `void engine.unlock()` immediately after construction — correct on
 *     Chrome/Firefox/Edge, where the autoplay policy keys on STICKY activation
 *     (the page has been interacted with), not on the current task;
 *   · `engine.attachUnlockGestures(window)` as well, so on a browser that
 *     insists on transient activation (older Safari) the NEXT pointerdown
 *     unlocks it synchronously.
 * Until one of those lands, every cue is dropped silently. A cue is a garnish;
 * queueing garnish to replay a second later is worse than not playing it.
 *
 * ── HOVER IS THE CUE THAT CAN RUIN A BUILD ──────────────────────────────────
 * Four rules, all of them load-bearing:
 *   1. ONE delegated listener on the document, not one per control. The shell
 *      is not React; it rebuilds its DOM wholesale on every navigation
 *      (`meta/shell.ts`), so a per-element listener is a leak per navigation.
 *   2. POINTER ONLY. `pointerover` fires for touch as well — a tap on a phone
 *      produces an `over` on the way to the `down`, so an unguarded hover cue
 *      DOUBLES every tap sound on mobile. `pointerType === 'mouse'` plus a
 *      `(hover: hover)` media check is the `@media (hover:hover)` semantics the
 *      CSS would give us.
 *   3. RATE LIMITED. Sweeping a pointer across a 20-card grid crosses twenty
 *      controls in half a second. `HOVER_MIN_MS` plus the table's own
 *      `minIntervalMs` guard keeps that to a tick, not a machine gun.
 *   4. REDUCED MOTION SILENCES IT. `prefers-reduced-motion` is the only signal
 *      this product has for "I do not want incidental feedback firing at me",
 *      and the repo already routes every incidental animation through
 *      `motionReduced()`. Hover is the one cue nobody asked for by pressing
 *      something, so it is the one cue that setting switches off. Tap,
 *      purchase, victory and the rest are consequences of a deliberate action
 *      and keep playing.
 *
 * Mute and the three volume sliders need no code here: `bindSfxEngineToPrefs`
 * points the engine's master gain at the SAME `audioPrefsStore` the Settings
 * panel writes, so Settings already governs every cue below.
 */
import { hapticForCue } from '@/engine/ui/haptics';
import { bindSfxEngineToPrefs } from './index';
import { audioPrefs } from './prefs';
import { playUiSound, unlockUiSounds, uiSoundBus, type UiSoundId } from './ui-sounds';
import { motionReduced } from '@/engine/ui/motion';

/* ═════════════════════════════════════════════════════════════ the cues ══ */

/**
 * Type-only views of the match mixer. `typeof import(...)` is erased by the
 * compiler, so naming these costs no runtime edge and cannot violate ADR-0005.
 */
type MatchAudioModule = typeof import('@/engine/match/audio/index');
type MatchAudioEngine = InstanceType<MatchAudioModule['AudioEngine']>;
type MatchSoundId = Parameters<MatchAudioEngine['play']>[0];

/** The cues with a match-mixer voice (the original vocabulary). */
export type MixerCue =
  | 'hover'
  | 'tap'
  | 'denied'
  | 'purchase'
  | 'reward'
  | 'enter_arena'
  | 'victory'
  | 'defeat';

/**
 * What the UI asks for, in product words. The interface cues added by the AAA
 * pass (`toggle`, `tab`, `modal_open`, ...) exist only on the UI sound bus.
 */
export type UiCue =
  | MixerCue
  | 'confirm'
  | 'toggle'
  | 'tab'
  | 'modal_open'
  | 'modal_close'
  | 'coin'
  | 'level_up'
  | 'notify';

/**
 * THE UI SOUND BUS OWNS EVERY INTERFACE CUE (`ui-sounds.ts`): its own context,
 * its own "Interface sounds" level, synth recipes that a recorded file at
 * /audio/ui/<id>.ogg replaces with no code change. The match mixer keeps the
 * three cues that ARE match moments — entering the arena, victory, defeat —
 * because those belong with the battle's own ducking and score.
 */
export const UI_CUE_BUS: Readonly<Record<UiCue, UiSoundId | null>> = Object.freeze({
  hover: 'hover',
  tap: 'click',
  denied: 'error',
  purchase: 'purchase',
  reward: 'reward',
  confirm: 'confirm',
  toggle: 'toggle',
  tab: 'tab',
  modal_open: 'modal_open',
  modal_close: 'modal_close',
  coin: 'coin',
  level_up: 'level_up',
  notify: 'notify',
  enter_arena: null,
  victory: null,
  defeat: null,
});

interface CueSpec {
  readonly sound: MatchSoundId;
  /** Multiplies the table's gain. Hover sits well under a deliberate press. */
  readonly gain?: number;
  /** Bypass the table's retrigger guard. Only for cues that must never vanish. */
  readonly force?: boolean;
}

/**
 * EVERY ENTRY IS AN EXISTING ID. Nothing here invents a sound; the right-hand
 * column is `SOUND_ATLAS`'s own vocabulary, so a recorded atlas dropped in via
 * `applySfxAtlas` re-skins the whole UI with no change on this side.
 */
export const UI_CUES: Readonly<Record<MixerCue, CueSpec>> = Object.freeze({
  // A short bright lift. Quiet: it fires from a pointer that is only passing.
  hover: Object.freeze({ sound: 'card_lift' as MatchSoundId, gain: 0.45 }),
  tap: Object.freeze({ sound: 'ui_tap' as MatchSoundId }),
  denied: Object.freeze({ sound: 'ui_denied' as MatchSoundId, force: true }),
  // The "something landed" chime. `force` because a purchase happening twice
  // inside the guard window is two purchases, and both deserve a confirmation.
  purchase: Object.freeze({ sound: 'mana_full' as MatchSoundId, force: true }),
  reward: Object.freeze({ sound: 'mana_pip' as MatchSoundId }),
  enter_arena: Object.freeze({ sound: 'deploy' as MatchSoundId, gain: 0.9, force: true }),
  victory: Object.freeze({ sound: 'match_win' as MatchSoundId, force: true }),
  defeat: Object.freeze({ sound: 'match_lose' as MatchSoundId, force: true }),
});

/* ══════════════════════════════════════════════════════ the shell mixer ══ */

let engine: MatchAudioEngine | null = null;
let loading: Promise<void> | null = null;
let unbindPrefs: (() => void) | null = null;
/** Set when the lazy import failed. Never retried; cues stay silent. */
let unavailable = false;

/**
 * Build the shell's mixer. Safe to call from anywhere and any number of times;
 * only the first call does work.
 *
 * MUST be reached from a user gesture for the audio to actually start — see the
 * autoplay note in the header. It never throws and never rejects: a browser
 * with no WebAudio, a chunk that will not download and a refused context all
 * land in the same place, which is silence.
 */
export function primeUiCues(): Promise<void> {
  // The interface bus needs no import: unlock it synchronously, while this is
  // still inside the gesture that called us.
  unlockUiSounds();
  if (engine !== null || unavailable) return Promise.resolve();
  if (loading !== null) return loading;
  loading = import('@/engine/match/audio/index')
    .then((mod) => {
      if (engine !== null) return;
      const prefs = audioPrefs();
      const next = new mod.AudioEngine({ muted: prefs.muted });
      engine = next;
      // Settings owns the level and the mute from here on, live.
      unbindPrefs = bindSfxEngineToPrefs(next);
      // Sticky-activation browsers start right now; the rest wait for the next
      // gesture, which this second handle catches.
      next.attachUnlockGestures(globalThis as unknown as Parameters<typeof next.attachUnlockGestures>[0]);
      void next.unlock();
    })
    .catch(() => {
      unavailable = true;
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

export interface PlayCueOptions {
  /**
   * Wait, briefly, for the mixer instead of dropping the cue.
   *
   * For the ONE-OFF cues that happen at the same instant as the gesture that
   * makes sound possible — entering the arena is the case this exists for. The
   * player taps Battle; that tap is both the navigation and the first gesture,
   * and the mixer is one `await import()` away from existing when the route
   * changes. Dropping the cue there would make "entering the arena is silent on
   * the first match of every session" a permanent, unexplainable bug.
   *
   * Bounded by `CUE_READY_TIMEOUT_MS` and at most one pending attempt per cue,
   * so a cue can never arrive so late that it lands on a different screen.
   */
  readonly whenReady?: boolean;
}

/** How long a `whenReady` cue will wait for the mixer before giving up. */
export const CUE_READY_TIMEOUT_MS = 1500;

const pending = new Set<UiCue>();

/**
 * Fire a cue. Returns true only if a voice actually started, which it will not
 * before the first gesture — that is the autoplay lock, not a failure.
 *
 * Never throws, never logs, and does nothing at all when the mixer has not been
 * primed. Every call site is free to fire and forget.
 */
export function playUiCue(cue: UiCue, options: PlayCueOptions = {}): boolean {
  const busId = UI_CUE_BUS[cue];
  if (busId !== undefined && busId !== null) {
    if (!uiSoundBus().unlocked) {
      if (options.whenReady === true) waitThenPlay(cue);
      return false;
    }
    return playUiSound(busId);
  }
  const spec = UI_CUES[cue as MixerCue];
  if (spec === undefined) return false;
  if (engine === null || !engine.unlocked) {
    if (options.whenReady === true) waitThenPlay(cue);
    return false;
  }
  try {
    return engine.play(spec.sound, {
      ...(spec.gain === undefined ? {} : { gain: spec.gain }),
      ...(spec.force === undefined ? {} : { force: spec.force }),
    });
  } catch {
    return false;
  }
}

function waitThenPlay(cue: UiCue): void {
  if (unavailable || pending.has(cue)) return;
  pending.add(cue);
  const deadline = Date.now() + CUE_READY_TIMEOUT_MS;
  const onBus = UI_CUE_BUS[cue] !== null;
  const attempt = (): void => {
    if (onBus ? uiSoundBus().unlocked : engine !== null && engine.unlocked) {
      pending.delete(cue);
      playUiCue(cue);
      return;
    }
    if (unavailable || Date.now() >= deadline) {
      pending.delete(cue);
      return;
    }
    setTimeout(attempt, 60);
  };
  // Priming here is harmless and usually already done; what this really waits
  // for is `unlock()`, which the window gesture handle in `primeUiCues` owns.
  void primeUiCues().then(() => setTimeout(attempt, 0));
}

/** True once the mixer exists AND the browser has let it make noise. */
export function uiCuesAudible(): boolean {
  return uiSoundBus().unlocked || (engine !== null && engine.unlocked);
}

/** Test seam / teardown: drop the mixer and its prefs subscription. */
export function resetUiCuesForTests(): void {
  unbindPrefs?.();
  unbindPrefs = null;
  engine?.destroy();
  engine = null;
  loading = null;
  unavailable = false;
}

/* ══════════════════════════════════════════════ the delegated listeners ══ */

/**
 * What counts as a control. Buttons and links, the shell's nav tabs (which are
 * `<button data-nav-to>`), anything wearing the shared press class, the card
 * tiles in the collection and the deck (`data-card-id` / `data-card-face`), and
 * an explicit opt-in for anything that is none of those.
 */
export const HOVER_CUE_SELECTOR =
  'button,a[href],summary,[role="button"],[role="tab"],[role="link"],[role="option"],' +
  '.rga-press,[data-nav-to],[data-card-id],[data-card-face],[data-hover-cue]';

/** Minimum gap between two hover cues, whatever the pointer is doing. */
export const HOVER_MIN_MS = 110;

function isDisabled(node: Element): boolean {
  return node.hasAttribute('disabled') || node.getAttribute('aria-disabled') === 'true';
}

/**
 * THE MATCH OWNS ITS OWN SOUND, and this listener is on the document.
 *
 * `startMatch` puts `rga-match` on its host and already fires `card_lift`,
 * `card_drop`, `ui_denied` and `emote` from the controls inside it, through its
 * OWN mixer with its own ducking. Letting the shell's delegate speak there too
 * would play two sounds for one press, from two AudioContexts, with the ducking
 * of neither applying to the other. So the shell falls silent inside the board.
 */
function insideMatch(node: Element): boolean {
  // An overlay drawn over the board that is INTERFACE rather than battle —
  // the end ceremony, the pre-match beats, the coach — opts back in with
  // data-ui-sound, so its buttons click like every other button.
  return node.closest('.rga-match') !== null && node.closest('[data-ui-sound]') === null;
}

/**
 * `@media (hover: hover)` in TypeScript. A device whose primary pointer cannot
 * hover must never make a hover sound — on a phone the `pointerover` that
 * precedes a tap would otherwise double every tap.
 */
function pointerCanHover(): boolean {
  try {
    const mm = globalThis.matchMedia;
    if (typeof mm !== 'function') return true;
    return mm.call(globalThis, '(hover: hover)').matches;
  } catch {
    return true;
  }
}

export interface UiCueDelegateOptions {
  /** Monotonic ms clock. Injected by tests. */
  readonly now?: () => number;
  /** Override the reduced-motion answer. Injected by tests. */
  readonly reducedMotion?: boolean;
  /** Override the hover-capability answer. Injected by tests. */
  readonly canHover?: boolean;
  /**
   * Where a cue goes. Defaults to `playUiCue`.
   *
   * A TEST SEAM, and it earns its keep: the mixer is module-private and exists
   * only after a gesture and a dynamic import, so without this the pointer-only
   * rule and the rate limit — the two things that can ruin a build — would be
   * unobservable in jsdom.
   */
  readonly play?: (cue: UiCue) => void;
}

/**
 * Install THE hover cue: one listener, on the document, for the life of the
 * page. Returns a detach handle.
 *
 * `pointerover` rather than `pointerenter` because only the former bubbles —
 * which is the whole reason one listener can serve a DOM that is rebuilt on
 * every navigation. Moving between two children of the SAME control re-fires
 * `pointerover`, so the resolved control is compared with the last one and a
 * move inside a button is not a second cue.
 */
export function installUiCueDelegate(
  target: Document | HTMLElement,
  options: UiCueDelegateOptions = {},
): () => void {
  const clock = options.now ?? (() => Date.now());
  const emit = options.play ?? ((cue: UiCue) => void playUiCue(cue));
  let lastAtMs = -Infinity;
  let lastControl: Element | null = null;

  const onOver = (event: Event): void => {
    const pe = event as PointerEvent;
    // Touch and pen produce `pointerover` too; only a mouse hovers.
    if (pe.pointerType !== undefined && pe.pointerType !== '' && pe.pointerType !== 'mouse') return;
    const canHover = options.canHover ?? pointerCanHover();
    if (!canHover) return;
    // The one setting that means "stop firing incidental feedback at me".
    if (options.reducedMotion ?? motionReduced()) return;

    const from = event.target;
    if (!(from instanceof Element)) return;
    const control = from.closest(HOVER_CUE_SELECTOR);
    if (control === null) {
      lastControl = null;
      return;
    }
    if (control === lastControl) return;
    lastControl = control;
    if (isDisabled(control) || insideMatch(control)) return;

    const at = clock();
    if (at - lastAtMs < HOVER_MIN_MS) return;
    lastAtMs = at;
    emit('hover');
  };

  // `pointerout` off a control clears the memo, so leaving and returning to the
  // same button is two hovers — which is what a player perceives.
  const onOut = (event: Event): void => {
    const to = (event as PointerEvent).relatedTarget;
    if (to instanceof Element && lastControl !== null && lastControl.contains(to)) return;
    lastControl = null;
  };

  target.addEventListener('pointerover', onOver, { passive: true });
  target.addEventListener('pointerout', onOut, { passive: true });
  return () => {
    target.removeEventListener('pointerover', onOver);
    target.removeEventListener('pointerout', onOut);
  };
}

/**
 * The press cue, on the same delegated principle.
 *
 * `pointerup`, NOT `click`: this project has shipped a dead control twice
 * because `PointerInput` preventDefaults its `pointerdown` over the match
 * canvas and the synthesised `click` never arrives (`engine/ui/press.ts` tells
 * the story). A cue that is silent exactly where the canvas is would be the
 * same bug with the volume down. `keyup` on Enter/Space covers the keyboard,
 * which produces no pointer events at all.
 */
export function installPressCueDelegate(
  target: Document | HTMLElement,
  options: Pick<UiCueDelegateOptions, 'play'> = {},
): () => void {
  const emit = options.play ?? ((cue: UiCue) => {
    // Every press is a gesture: (re)unlock here so a context Safari suspended
    // in the background comes back on the first tap after it.
    unlockUiSounds();
    void playUiCue(cue);
  });
  const fire = (node: Element | null): void => {
    if (node === null || isDisabled(node) || insideMatch(node)) return;
    const cue = pressCueFor(node);
    if (cue === null) return;
    emit(cue);
    // A key confirm (gold CTA, purchase, claim, refusal) is also FELT on a
    // phone. Guarded three ways inside — API, coarse pointer, player setting.
    hapticForCue(cue);
  };
  // A checkbox or radio inside a label never reaches the selector above (the
  // label is what is pressed), so the form controls speak on CHANGE instead:
  // a switch latches, a segmented option behaves like a tab.
  const onChange = (event: Event): void => {
    const input = event.target;
    // A <select> settling on a new option speaks like a segmented choice.
    if (input instanceof HTMLSelectElement) {
      if (input.disabled || insideMatch(input) || input.closest('[data-sfx="none"]') !== null) return;
      emit('tab');
      return;
    }
    if (!(input instanceof HTMLInputElement) || insideMatch(input) || input.disabled) return;
    if (input.closest('[data-sfx="none"]') !== null) return;
    if (input.type === 'checkbox') emit('toggle');
    else if (input.type === 'radio') emit('tab');
    else if (input.type === 'range') emit('tap');
  };
  const onUp = (event: Event): void => {
    const from = event.target;
    if (!(from instanceof Element)) return;
    fire(from.closest(HOVER_CUE_SELECTOR));
  };
  const onKey = (event: Event): void => {
    const ke = event as KeyboardEvent;
    if (ke.key !== 'Enter' && ke.key !== ' ' && ke.key !== 'Spacebar') return;
    const from = event.target;
    if (!(from instanceof Element)) return;
    fire(from.closest(HOVER_CUE_SELECTOR));
  };
  target.addEventListener('pointerup', onUp, { passive: true });
  target.addEventListener('keyup', onKey, { passive: true });
  target.addEventListener('change', onChange, { passive: true });
  return () => {
    target.removeEventListener('pointerup', onUp);
    target.removeEventListener('keyup', onKey);
    target.removeEventListener('change', onChange);
  };
}

/** `data-sfx` values a control may declare, mapped to cues. */
const SFX_CUE: Readonly<Record<string, UiCue | null>> = Object.freeze({
  click: 'tap',
  tap: 'tap',
  confirm: 'confirm',
  toggle: 'toggle',
  tab: 'tab',
  error: 'denied',
  purchase: 'purchase',
  reward: 'reward',
  coin: 'coin',
  modal_open: 'modal_open',
  modal_close: 'modal_close',
  level_up: 'level_up',
  notify: 'notify',
  none: null,
});

/**
 * WHICH SOUND A PRESS MAKES. A control may say so itself (`data-sfx`, which
 * the button system writes per variant — the gold CTA confirms); otherwise
 * the role decides: a tab sounds like a tab, a switch latches, and everything
 * else is the wooden click. Returns null for a control that asked for silence.
 */
export function pressCueFor(node: Element): UiCue | null {
  const declared = node.getAttribute('data-sfx');
  if (declared !== null) {
    const cue = SFX_CUE[declared];
    return cue === undefined ? 'tap' : cue;
  }
  // The gold call to action confirms, whichever way it was skinned.
  if (node.classList.contains('rga-btn--primary') || node.classList.contains('rga-plate--primary')) return 'confirm';
  if (node.classList.contains('rga-btn--danger')) return 'tap';
  const role = node.getAttribute('role');
  if (role === 'tab') return 'tab';
  if (role === 'switch' || node.getAttribute('aria-pressed') !== null) return 'toggle';
  if (node.hasAttribute('data-close')) return 'modal_close';
  return 'tap';
}
